import {Component, ElementRef, Injector, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Bill} from '../bill';
import {UserService} from '../../../services/user.service';
import {BillService} from '../../bill.service';
import {GlobalErrorHandlerService} from '../../../services/global-error.service';
import {LoaderService} from '../../../services/loader.service';
import {UpdoxMessagesService} from '../../../routes/practice-site/updox-messaging/updox-messages.service';
import {UpdoxDirectMessage} from '../../../routes/practice-site/updox-messaging/updox-direct-message';
import {ContactSearchResult} from '../../../routes/practice-site/updox-messaging/updox-contact';
import {AlertService} from '../../../_alert';
import html2canvas from 'html2canvas';
import * as jsPDF from 'jspdf';

@Component({
  selector: 'app-print-bill',
  templateUrl: './print-bill.component.html',
  styleUrls: ['./print-bill.component.scss']
})
export class PrintBillComponent implements OnInit, OnDestroy {
  @ViewChild('receipt', { static: false }) receipt: ElementRef | undefined;
  @ViewChild('sendModal') sendModal: any;

  public bill: Bill;
  public print_type: string;
  public today: Date = new Date();
  public report_month: any;
  public report_date: any;
  public year: any;
  public report_for: any;
  public programme_category: any;
  public reportDetails: any = {};
  public selectedCarePlans: any;
  public isBilling: boolean;

  // Send as secure message state
  sendToAddress = '';
  sendSubject = '';
  sendBody = '';
  pdfFile: File | null = null;
  isGeneratingPdf = false;
  isSending = false;
  toSearchResults: ContactSearchResult[] = [];
  toSearchLoading = false;
  showToDropdown = false;
  private toSearchTimer: any;
  private searchVersion = 0;
  private practiceId: number;

  constructor(public injector: Injector, public userService: UserService, public billService: BillService,
              public globalErrorHandlerService: GlobalErrorHandlerService, public loaderService: LoaderService,
              private messagesService: UpdoxMessagesService, private alertService: AlertService) { }

  ngOnInit(): void {
    const user = this.userService.current_user;
    this.practiceId = (user.practices_id && user.practices_id.length > 0)
      ? user.practices_id[0]
      : (user.profileable as any)?.practice_id || user.profileable_id;

    this.bill = this.injector.get('bill', null);
    this.print_type = this.injector.get('type', null);
    this.report_for = this.injector.get('report_for', null);
    this.report_month = this.injector.get('report_month', null);
    this.selectedCarePlans = this.injector.get('selectedCarePlans', null);
    this.isBilling = this.injector.get('isBilling', false);
    this.year = this.injector.get('year', null);
    this.report_date = this.injector.get('report_date', null);
    this.programme_category = this.injector.get('programme_category', null);
    if (this.report_for) {
      this.getReportData(this.report_for);
    } else {
      this.loaderService.hide();
    }
  }

  ngOnDestroy(): void {
    clearTimeout(this.toSearchTimer);
  }

  generatePdf(action) {
    this.loaderService.show()
    this.receipt.nativeElement.style.height = this.receipt.nativeElement.style.offsetHeight;
    html2canvas(this.receipt.nativeElement, {
      width: this.receipt.nativeElement.offsetWidth,
      height: this.receipt.nativeElement.offsetHeight})
      .then((canvas) => {
        let imgData = canvas.toDataURL('image/png');
        let imgWidth = 210;
        let pageHeight = 295;
        let imgHeight = canvas.height * imgWidth / canvas.width;
        let heightLeft = imgHeight;
        let doc = new jsPDF('p', 'mm');
        let position = 0;

        doc.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        while (heightLeft >= 0) {
          position = heightLeft - imgHeight;
          doc.addPage();
          doc.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }
        if (action === 'print') {
          doc.autoPrint();
          window.open(doc.output('bloburl'), '_blank');
        } else {
          doc.save( 'file.pdf');
        }
        this.loaderService.hide();
      });
  }

  private buildPdfDoc(canvas: HTMLCanvasElement): any {
    const imgData = canvas.toDataURL('image/png');
    const imgWidth = 210;
    const pageHeight = 295;
    const imgHeight = canvas.height * imgWidth / canvas.width;
    let heightLeft = imgHeight;
    const doc = new jsPDF('p', 'mm');
    let position = 0;
    doc.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
    while (heightLeft >= 0) {
      position = heightLeft - imgHeight;
      doc.addPage();
      doc.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }
    return doc;
  }

  openSendModal(): void {
    if (!this.receipt || this.isGeneratingPdf) { return; }
    this.isGeneratingPdf = true;
    // setTimeout(0) gives Angular one rendering frame to paint the spinner
    // on the button before html2canvas blocks the main thread.
    setTimeout(() => {
    html2canvas(this.receipt.nativeElement, {
      width: this.receipt.nativeElement.offsetWidth,
      height: this.receipt.nativeElement.offsetHeight
    }).then((canvas) => {
      const doc = this.buildPdfDoc(canvas);
      const blob: Blob = doc.output('blob');
      const patientName = (this.bill?.name || 'Patient').replace(/\s+/g, '-');
      const month = this.report_date
        ? new Date(this.report_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }).replace(' ', '-')
        : '';
      const filename = `Superbill-${patientName}-${month}.pdf`;
      this.pdfFile = new File([blob], filename, { type: 'application/pdf' });
      this.sendToAddress = '';
      this.sendSubject = `Superbill - ${this.bill?.name || 'Patient'}${month ? ' - ' + month : ''}`;
      this.sendBody = '';
      this.toSearchResults = [];
      this.showToDropdown = false;
      this.toSearchLoading = false;
      this.isGeneratingPdf = false;
      this.sendModal.show();
    }).catch(() => {
      this.isGeneratingPdf = false;
      this.globalErrorHandlerService.error = 'Failed to generate PDF. Please try again.';
    });
    }); // end setTimeout
  }

  onSendModalHide(): void {
    this.isSending = false;
    this.showToDropdown = false;
    clearTimeout(this.toSearchTimer);
    this.toSearchLoading = false;
  }

  onToInput(value: string): void {
    clearTimeout(this.toSearchTimer);
    this.toSearchResults = [];
    this.showToDropdown = false;
    if (!value || value.trim().length < 2) { this.toSearchLoading = false; return; }
    this.toSearchLoading = true;
    this.toSearchTimer = setTimeout(() => this.runContactSearch(value.trim()), 300);
  }

  private runContactSearch(query: string): void {
    const version = ++this.searchVersion;
    this.messagesService.searchContacts(this.practiceId, query)
      .then(resp => {
        if (version !== this.searchVersion) { return; }
        this.toSearchLoading = false;
        const local: ContactSearchResult[] = (resp.wellwink_contacts || []).map((c: any) => ({
          id: c.id, name: c.name || '', direct_address: c.direct_address || '',
          organization: c.organization || '', specialty: c.specialty || '', source: 'local' as const,
        }));
        const directory: ContactSearchResult[] = this.normalizeDirectoryResults(resp.directory_results);
        this.toSearchResults = [...local, ...directory];
        this.showToDropdown = this.toSearchResults.length > 0;
      })
      .catch(() => { if (version !== this.searchVersion) { return; } this.toSearchLoading = false; });
  }

  private normalizeDirectoryResults(raw: any): ContactSearchResult[] {
    if (!raw) { return []; }
    const items: any[] = Array.isArray(raw) ? raw : (Array.isArray(raw.searchResults) ? raw.searchResults : []);
    return items
      .map(hit => ({
        name: (hit.firstName || hit.lastName) ? `${hit.firstName || ''} ${hit.lastName || ''}`.trim() : hit.displayName || hit.name || '',
        direct_address: hit.directAddress || hit.direct_address || '',
        organization: hit.businessName || hit.organization || '',
        specialty: hit.specialty || '',
        source: 'directory' as const,
      }))
      .filter(r => !!r.direct_address);
  }

  selectContact(result: ContactSearchResult): void {
    this.sendToAddress = result.direct_address;
    this.toSearchResults = [];
    this.showToDropdown = false;
  }

  closeDropdownDelayed(): void {
    setTimeout(() => { this.showToDropdown = false; }, 150);
  }

  get localContacts(): ContactSearchResult[] { return this.toSearchResults.filter(r => r.source === 'local'); }
  get directoryContacts(): ContactSearchResult[] { return this.toSearchResults.filter(r => r.source === 'directory'); }

  sendMessage(): void {
    if (!this.sendToAddress.trim() || !this.pdfFile) { return; }
    this.isSending = true;
    const message = new UpdoxDirectMessage();
    message.practice_id = this.practiceId;
    message.to_address  = this.sendToAddress.trim();
    message.subject     = this.sendSubject;
    message.body_text   = this.sendBody;
    message.patient_id  = this.bill?.patient_json?.id || null;
    this.messagesService.sendMessage(message, [this.pdfFile])
      .then(resp => {
        this.isSending = false;
        const updoxResp = resp?.updox_response;
        if (updoxResp && !updoxResp.successful) {
          this.globalErrorHandlerService.error = updoxResp.responseMessage || 'Failed to send message.';
          return;
        }
        this.alertService.success('Superbill sent successfully.', this.alertService.options);
        this.sendModal.hide();
        this.pdfFile = null;
      })
      .catch(resp => {
        this.isSending = false;
        const body = resp?.error;
        this.globalErrorHandlerService.error = body?.responseMessage || body?.error || 'Failed to send message. Please try again.';
      });
  }


  public getReportData(report_for: any) {
    let patient_id = this.bill.patient_json.id;
    let enrolment_id = this.bill.enrollment_id;
    let params = {report_for: this.report_for, month: this.report_month, patient_id: patient_id , enrolment_id: enrolment_id,
      selectedCarePlans: this.selectedCarePlans,
      year: this.year}
    this.billService.getDetailBillReport(params).then(resp => {
      this.reportDetails = resp;
      if (!this.isBilling) {
        this.bill = resp['bill'];
      }
      this.loaderService.hide();
    }).catch(resp => {
      this.loaderService.hide();
      this.globalErrorHandlerService.error = resp.error.error;
    })
  }
}
